import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  TextField,
  Autocomplete,
  CircularProgress,
  Popover,
} from '@mui/material';
import {
  Clear as ClearIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import TagTreeFilter from '../../../customControls/TagTreeFilter';
import useDebouncedSearch from '../hooks/useDebouncedSearch';

const FILTER_SEARCH_MIN_LENGTH = 1;

/**
 * Recursively get all descendant tag IDs for a given tag
 * @param {string} tagId - The parent tag ID to find descendants for
 * @param {Array} tags - Array of all tags with parentId properties
 * @returns {Array} Array of descendant tag IDs (strings)
 */
const getAllDescendantTagIds = (tagId, tags) => {
  const descendants = [];
  const tagIdStr = String(tagId);

  // Find direct children of this tag
  const directChildren = tags.filter((tag) => String(tag.parentId) === tagIdStr);

  // For each child, add its ID and recursively get its descendants
  directChildren.forEach((child) => {
    const childIdStr = String(child.id);
    descendants.push(childIdStr);
    descendants.push(...getAllDescendantTagIds(childIdStr, tags));
  });

  return descendants;
};

const FilterPanel = ({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  activeFilters = [],
  onSearchLocations,
  onSearchAssets,
  onSearchZones,
  onSearchComponents,
  onSearchConditions,
  repairedStatusFilter,
  // Redux-based search results (optional - for use without .unwrap())
  assetSearchResults,
  assetSearchLoading,
  locationSearchResults,
  locationSearchLoading,
}) => {
  const { messages } = useIntl();
  // State for controlling dropdown open/close
  const [inspectionTypeOpen, setInspectionTypeOpen] = useState(false);
  const [assetTypeOpen, setAssetTypeOpen] = useState(false);
  const [tagSearchValue, setTagSearchValue] = useState('');
  const [tagDropdownAnchorEl, setTagDropdownAnchorEl] = useState(null);
  const tagDropdownOpen = Boolean(tagDropdownAnchorEl);
  const selectedTagIdsSet = useMemo(() => new Set((filters.tagIds || []).map(String)), [filters.tagIds]);

  // Use debounced search hook for each searchable field
  // If external results are provided, use Redux mode; otherwise use legacy callback mode
  const locationSearch = useDebouncedSearch(onSearchLocations, {
    externalOptions: locationSearchResults,
    externalLoading: locationSearchLoading,
  });
  const assetSearch = useDebouncedSearch(onSearchAssets, {
    minLength: FILTER_SEARCH_MIN_LENGTH,
    externalOptions: assetSearchResults,
    externalLoading: assetSearchLoading,
  });
  const zoneSearch = useDebouncedSearch(onSearchZones, { minLength: FILTER_SEARCH_MIN_LENGTH });
  const componentSearch = useDebouncedSearch(onSearchComponents, { minLength: FILTER_SEARCH_MIN_LENGTH });
  const conditionSearch = useDebouncedSearch(onSearchConditions, { minLength: FILTER_SEARCH_MIN_LENGTH });

  const handleChange = (field, value) => {
    onFilterChange({ ...filters, [field]: value });
  };

  const handleTagSelect = (tag) => {
    if (!tag) return;
    const current = new Set((filters.tagIds || []).map(String));
    const tagId = String(tag.id);

    // Get all descendant tag IDs for parent-child cascading selection
    const descendantIds = getAllDescendantTagIds(tagId, filterOptions.tags || []);
    const allIdsToAdd = [tagId, ...descendantIds];

    // Add the selected tag and all its descendants
    allIdsToAdd.forEach((id) => current.add(id));

    handleChange('tagIds', Array.from(current));
    handleCloseTagDropdown();
    setTagSearchValue('');
  };

  const handleOpenTagDropdown = (target) => {
    if (!tagDropdownOpen && target) {
      const anchor = target.parentElement || target;
      setTagDropdownAnchorEl(anchor);
    }
  };

  const handleCloseTagDropdown = () => {
    setTagDropdownAnchorEl(null);
  };

  const handleRemoveFilter = (field, valueToRemove) => {
    const newFilters = { ...filters };
    // Handle multi-select fields (configTypeIds and assetTypeIds are arrays)
    if (field === 'configTypeIds' && valueToRemove !== undefined) {
      const currentValues = filters.configTypeIds || [];
      const updatedValues = currentValues.filter((id) => id !== valueToRemove);
      if (updatedValues.length === 0) {
        delete newFilters.configTypeIds;
      } else {
        newFilters.configTypeIds = updatedValues;
      }
    } else if (field === 'assetTypeIds' && valueToRemove !== undefined) {
      const currentValues = filters.assetTypeIds || [];
      const updatedValues = currentValues.filter((id) => id !== valueToRemove);
      if (updatedValues.length === 0) {
        delete newFilters.assetTypeIds;
      } else {
        newFilters.assetTypeIds = updatedValues;
      }
    } else if (field === 'tagIds' && valueToRemove !== undefined) {
      const currentTags = filters.tagIds || [];
      const updatedTags = currentTags.filter((id) => id !== valueToRemove);
      if (updatedTags.length === 0) {
        delete newFilters.tagIds;
      } else {
        newFilters.tagIds = updatedTags;
      }
    } else if (field === 'locationIds' && valueToRemove !== undefined) {
      const currentLocations = filters.locations || [];
      const updatedLocations = currentLocations.filter((l) => l.id !== valueToRemove);
      if (updatedLocations.length === 0) {
        delete newFilters.locations;
      } else {
        newFilters.locations = updatedLocations;
      }
    } else if (field === 'assetIds' && valueToRemove !== undefined) {
      const currentAssets = filters.assets || [];
      const updatedAssets = currentAssets.filter((a) => a.id !== valueToRemove);
      if (updatedAssets.length === 0) {
        delete newFilters.assets;
      } else {
        newFilters.assets = updatedAssets;
      }
    } else if (field === 'zoneIds' && valueToRemove !== undefined) {
      const currentZones = filters.zones || [];
      const updatedZones = currentZones.filter((z) => z.id !== valueToRemove);
      if (updatedZones.length === 0) {
        delete newFilters.zones;
      } else {
        newFilters.zones = updatedZones;
      }
    } else if (field === 'componentIds' && valueToRemove !== undefined) {
      const currentComponents = filters.components || [];
      const updatedComponents = currentComponents.filter((c) => c.id !== valueToRemove);
      if (updatedComponents.length === 0) {
        delete newFilters.components;
      } else {
        newFilters.components = updatedComponents;
      }
    } else if (field === 'conditionIds' && valueToRemove !== undefined) {
      const currentConditions = filters.conditions || [];
      const updatedConditions = currentConditions.filter((c) => c.id !== valueToRemove);
      if (updatedConditions.length === 0) {
        delete newFilters.conditions;
      } else {
        newFilters.conditions = updatedConditions;
      }
    } else {
      delete newFilters[field];
    }
    onFilterChange(newFilters);
  };

  return (
    <Box className="filter-section">
      <Box className="filter-row">
        {/* Repair status filter passed from parent */}
        {repairedStatusFilter}

        {filterOptions.configTypes && (
          <FormControl className="filter-field" size="small">
            <InputLabel shrink={false}>Inspection Type</InputLabel>
            <Select
              multiple
              open={inspectionTypeOpen}
              onOpen={() => setInspectionTypeOpen(true)}
              onClose={() => setInspectionTypeOpen(false)}
              value={filters.configTypeIds || []}
              onChange={(e) => {
                handleChange('configTypeIds', e.target.value);
                setInspectionTypeOpen(false);
              }}
              renderValue={() => ''}
              notched={false}
            >
              {filterOptions.configTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {(filterOptions.locations || onSearchLocations) && (
          <Autocomplete
            className="filter-field"
            size="small"
            options={onSearchLocations ? locationSearch.options : (filterOptions.locations || [])}
            getOptionLabel={(option) => option.name || ''}
            value={null}
            inputValue={locationSearch.inputValue}
            onChange={(e, newValue) => {
              if (newValue) {
                const currentLocations = filters.locations || [];
                const alreadySelected = currentLocations.some((l) => l.id === newValue.id);
                if (!alreadySelected) {
                  onFilterChange({
                    ...filters,
                    locations: [...currentLocations, { id: newValue.id, name: newValue.name }],
                  });
                }
              }
              locationSearch.setInputValue('');
            }}
            onInputChange={(e, newInputValue, reason) => {
              if (reason !== 'reset') {
                locationSearch.setInputValue(newInputValue);
              }
            }}
            loading={locationSearch.loading}
            noOptionsText={onSearchLocations ? (locationSearch.inputValue.length < 2 ? 'Type to search...' : 'No locations found') : 'No locations available'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {locationSearch.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            filterOptions={onSearchLocations ? (x) => x : undefined}
          />
        )}

        {(filterOptions.assets || onSearchAssets) && (
          <Autocomplete
            className="filter-field"
            size="small"
            options={onSearchAssets ? assetSearch.options : (filterOptions.assets || [])}
            getOptionLabel={(option) => option.fleet || ''}
            value={null}
            inputValue={assetSearch.inputValue}
            onChange={(e, newValue) => {
              if (newValue) {
                const currentAssets = filters.assets || [];
                const alreadySelected = currentAssets.some((a) => a.id === newValue.id);
                if (!alreadySelected) {
                  onFilterChange({
                    ...filters,
                    assets: [...currentAssets, { id: newValue.id, fleet: newValue.fleet }],
                  });
                }
              }
              assetSearch.setInputValue('');
            }}
            onInputChange={(e, newInputValue, reason) => {
              if (reason !== 'reset') {
                assetSearch.setInputValue(newInputValue);
              }
            }}
            loading={assetSearch.loading}
            noOptionsText={onSearchAssets ? (assetSearch.inputValue.trim().length < FILTER_SEARCH_MIN_LENGTH ? 'Type to search...' : 'No assets found') : 'No assets available'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Asset"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {assetSearch.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            filterOptions={onSearchAssets ? (x) => x : undefined}
          />
        )}

        {filterOptions.assetTypes && (
          <FormControl className="filter-field" size="small">
            <InputLabel shrink={false}>Asset Type</InputLabel>
            <Select
              multiple
              open={assetTypeOpen}
              onOpen={() => setAssetTypeOpen(true)}
              onClose={() => setAssetTypeOpen(false)}
              value={filters.assetTypeIds || []}
              onChange={(e) => {
                handleChange('assetTypeIds', e.target.value);
                setAssetTypeOpen(false);
              }}
              renderValue={() => ''}
              notched={false}
            >
              {filterOptions.assetTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {filterOptions.tags?.length > 0 && (
          <>
            <TextField
              className="filter-field"
              size="small"
              label="Tag"
              placeholder="Search tag"
              value={tagSearchValue}
              onChange={(e) => setTagSearchValue(e.target.value)}
              onFocus={(event) => handleOpenTagDropdown(event.currentTarget)}
              onClick={(event) => handleOpenTagDropdown(event.currentTarget)}
              InputProps={{
                endAdornment: <ArrowDropDownIcon fontSize="small" />,
              }}
            />
            <Popover
              open={tagDropdownOpen}
              anchorEl={tagDropdownAnchorEl}
              onClose={handleCloseTagDropdown}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              disableAutoFocus
              disableEnforceFocus
              disableRestoreFocus
            >
              <Box sx={{ p: 1.5, width: 320 }}>
                <TagTreeFilter
                  tags={filterOptions.tags}
                  searchText={tagSearchValue}
                  selectedTagIds={selectedTagIdsSet}
                  onTagSelect={handleTagSelect}
                  onTagDeselect={undefined}
                  height={260}
                />
              </Box>
            </Popover>
          </>
        )}

        {/* Independent Component filter (not cascading from zones) - appears when onSearchComponents is provided without zone filters */}
        {onSearchComponents && !onSearchZones && !filterOptions.zones && (
          <Autocomplete
            className="filter-field"
            size="small"
            options={componentSearch.options}
            getOptionLabel={(option) => option.name || ''}
            value={null}
            inputValue={componentSearch.inputValue}
            onChange={(e, newValue) => {
              if (newValue) {
                const currentComponents = filters.components || [];
                const alreadySelected = currentComponents.some((c) => c.id === newValue.id);
                if (!alreadySelected) {
                  onFilterChange({
                    ...filters,
                    components: [...currentComponents, { id: newValue.id, name: newValue.name }],
                  });
                }
              }
              componentSearch.setInputValue('');
            }}
            onInputChange={(e, newInputValue, reason) => {
              if (reason !== 'reset') {
                componentSearch.setInputValue(newInputValue);
              }
            }}
            loading={componentSearch.loading}
            noOptionsText={componentSearch.inputValue.trim().length < FILTER_SEARCH_MIN_LENGTH ? 'Type to search...' : 'No components found'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Component"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {componentSearch.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            filterOptions={(x) => x}
          />
        )}

        {filterOptions.defectStatuses && (
          <FormControl className="filter-field" size="small">
            <InputLabel>Defect Status</InputLabel>
            <Select
              value={filters.defectStatus || -8}
              label="Defect Status"
              onChange={(e) => handleChange('defectStatus', e.target.value)}
            >
              <MenuItem value={-8}>{messages['showAll']}</MenuItem>
              <MenuItem value={-10}>{messages['open']}</MenuItem>
              <MenuItem value={-6}>{messages['pending']}</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Zone filter: Autocomplete for search, Select for cascading static options */}
        {onSearchZones && (
          <Autocomplete
            className="filter-field"
            size="small"
            options={zoneSearch.options}
            getOptionLabel={(option) => option.name || ''}
            value={null}
            inputValue={zoneSearch.inputValue}
            onChange={(e, newValue) => {
              if (newValue) {
                const currentZones = filters.zones || [];
                const alreadySelected = currentZones.some((z) => z.id === newValue.id);
                if (!alreadySelected) {
                  onFilterChange({
                    ...filters,
                    zones: [...currentZones, { id: newValue.id, name: newValue.name }],
                  });
                }
              }
              zoneSearch.setInputValue('');
            }}
            onInputChange={(e, newInputValue, reason) => {
              if (reason !== 'reset') {
                zoneSearch.setInputValue(newInputValue);
              }
            }}
            loading={zoneSearch.loading}
            noOptionsText={zoneSearch.inputValue.trim().length < FILTER_SEARCH_MIN_LENGTH ? 'Type to search...' : 'No zones found'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Zone"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {zoneSearch.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            filterOptions={(x) => x}
          />
        )}
        {!onSearchZones && filterOptions.zones && (
          <FormControl className="filter-field" size="small">
            <InputLabel id="zone-select-label">Zone</InputLabel>
            <Select
              labelId="zone-select-label"
              value={filters.zoneId || ''}
              label="Zone"
              onChange={(e) => {
                const selected = filterOptions.zones.find((z) => z.id === e.target.value);
                onFilterChange({
                  ...filters,
                  zoneId: e.target.value,
                  zoneName: selected?.name || '',
                });
              }}
            >
              <MenuItem value="">All Zones</MenuItem>
              {[...filterOptions.zones]
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                .map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {/* Component filter: Autocomplete for search (only in cascading context with zones), Select for cascading static options */}
        {onSearchComponents && (onSearchZones || filterOptions.zones) && (
          <Autocomplete
            className="filter-field"
            size="small"
            options={componentSearch.options}
            getOptionLabel={(option) => option.name || ''}
            value={null}
            inputValue={componentSearch.inputValue}
            onChange={(e, newValue) => {
              if (newValue) {
                const currentComponents = filters.components || [];
                const alreadySelected = currentComponents.some((c) => c.id === newValue.id);
                if (!alreadySelected) {
                  onFilterChange({
                    ...filters,
                    components: [...currentComponents, { id: newValue.id, name: newValue.name }],
                  });
                }
              }
              componentSearch.setInputValue('');
            }}
            onInputChange={(e, newInputValue, reason) => {
              if (reason !== 'reset') {
                componentSearch.setInputValue(newInputValue);
              }
            }}
            loading={componentSearch.loading}
            noOptionsText={componentSearch.inputValue.trim().length < FILTER_SEARCH_MIN_LENGTH ? 'Type to search...' : 'No components found'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Component"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {componentSearch.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            filterOptions={(x) => x}
          />
        )}
        {!onSearchComponents && filterOptions.components && (
          <FormControl className="filter-field" size="small">
            <InputLabel id="component-select-label">Component</InputLabel>
            <Select
              labelId="component-select-label"
              value={filters.componentId || ''}
              label="Component"
              onChange={(e) => {
                const selected = filterOptions.components.find((c) => c.id === e.target.value);
                onFilterChange({
                  ...filters,
                  componentId: e.target.value,
                  componentName: selected?.name || '',
                });
              }}
            >
              <MenuItem value="">All Components</MenuItem>
              {[...filterOptions.components]
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                .map((component) => (
                  <MenuItem key={component.id} value={component.id}>
                    {component.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {/* Condition filter: Autocomplete for search, Autocomplete with static sorted options for cascading */}
        {onSearchConditions && (
          <Autocomplete
            className="filter-field"
            size="small"
            options={conditionSearch.options}
            getOptionLabel={(option) => option.name || ''}
            value={null}
            inputValue={conditionSearch.inputValue}
            onChange={(e, newValue) => {
              if (newValue) {
                const currentConditions = filters.conditions || [];
                const alreadySelected = currentConditions.some((c) => c.id === newValue.id);
                if (!alreadySelected) {
                  onFilterChange({
                    ...filters,
                    conditions: [...currentConditions, { id: newValue.id, name: newValue.name }],
                  });
                }
              }
              conditionSearch.setInputValue('');
            }}
            onInputChange={(e, newInputValue, reason) => {
              if (reason !== 'reset') {
                conditionSearch.setInputValue(newInputValue);
              }
            }}
            loading={conditionSearch.loading}
            noOptionsText={conditionSearch.inputValue.length < FILTER_SEARCH_MIN_LENGTH ? 'Type to search...' : 'No conditions found'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Condition"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {conditionSearch.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            filterOptions={(x) => x}
          />
        )}
        {!onSearchConditions && filterOptions.conditions && (
          <FormControl className="filter-field" size="small">
            <InputLabel id="condition-select-label">Condition</InputLabel>
            <Select
              labelId="condition-select-label"
              value={filters.conditionId || ''}
              label="Condition"
              onChange={(e) => {
                const selected = filterOptions.conditions.find((c) => c.id === e.target.value);
                onFilterChange({
                  ...filters,
                  conditionId: e.target.value,
                  conditionName: selected?.name || '',
                });
              }}
            >
              <MenuItem value="">All Conditions</MenuItem>
              {[...filterOptions.conditions]
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                .map((condition) => (
                  <MenuItem key={condition.id} value={condition.id}>
                    {condition.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {filterOptions.showDateRange && (
          <>
            <TextField
              className="filter-field"
              label="Start Date"
              type="date"
              size="small"
              value={filters.startDate || ''}
              onChange={(e) => handleChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className="filter-field"
              label="End Date"
              type="date"
              size="small"
              value={filters.endDate || ''}
              onChange={(e) => handleChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}

        <Box className="filter-actions">
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={onClearFilters}
            disabled={activeFilters.length === 0}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {activeFilters.length > 0 && (
        <Box className="active-filters">
          {activeFilters.map((filter) => (
            <Chip
              key={filter.filterKey || filter.field}
              label={`${filter.label}: ${filter.value}`}
              size="small"
              onDelete={() => handleRemoveFilter(filter.field, filter.removeValue)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FilterPanel;
